import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Flame, Link2 } from 'lucide-react';
import TopTickers from '../ui/TopTickers';
import SpikeTopTickers from '../ui/SpikeTopTickers';
import TopPriceLinkageTable from '../ui/TopPriceLinkageTable';
import { PageHeader } from '../ui/PageHeader';
import BlurredSection from '../ui/BlurredSection';
import UpgradeModal from '../ui/UpgradeModal';
import { useAuth } from '../../contexts/AuthContext';

type DashboardTab = 'trending' | 'spikes' | 'linkage';

function Dashboard() {
  const { isPro } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DashboardTab>('trending');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const tabs: { id: DashboardTab; label: string; icon: typeof TrendingUp; subtitle: string }[] = [
    { id: 'trending', label: 'Trending', icon: TrendingUp, subtitle: 'By mention volume' },
    { id: 'spikes', label: 'Spikes', icon: Flame, subtitle: 'Growing interest' },
    { id: 'linkage', label: 'Price Linkage', icon: Link2, subtitle: 'Reddit vs 1h return' },
  ];

  return (
    <div className="content">
      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-8 max-w-6xl">
        <div className="pl-0 sm:pl-4 mb-4 sm:mb-6">
          <PageHeader
            title="Trending Now"
            subtitle="Live • Market overview"
            icon={TrendingUp}
          />
        </div>

        {/* Tabs — overflow-y-hidden чтобы убрать микро-скролл на мобильном от border/высоты */}
        <div className="flex gap-0 sm:gap-1 mb-6 border-b border-white/10 overflow-x-auto overflow-y-hidden -mx-4 px-4 sm:mx-0 sm:px-0 min-h-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 rounded-t-lg font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 border-b-2 border-transparent
                  ${isActive
                    ? 'bg-white/10 text-brand-gold border-brand-gold -mb-px'
                    : 'text-white/60 hover:text-white/80 hover:bg-white/5'}
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {activeTab === 'trending' && (
          <div className="rounded-2xl p-6">
            <TopTickers />
          </div>
        )}
        {activeTab === 'spikes' && (
          <div className="rounded-2xl overflow-hidden">
            {isPro ? (
              <SpikeTopTickers />
            ) : (
              <BlurredSection
                isPro={false}
                onUpgrade={() => setShowUpgradeModal(true)}
                title="Spike detection - Premium"
              >
                <div className="dashboard-locked-preview rounded-2xl p-6 min-h-[420px]">
                  <div className="flex items-center gap-2 mb-4">
                    <Flame className="w-5 h-5 text-brand-gold" />
                    <h2 className="text-xl font-semibold text-white">Growing interest (by spike)</h2>
                  </div>
                  <p className="text-white/60 text-sm mb-4">Tickers with sudden spikes in mentions and unique users.</p>
                  <div className="space-y-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-10 rounded-lg bg-white/10 border border-white/10" />
                    ))}
                  </div>
                </div>
              </BlurredSection>
            )}
          </div>
        )}
        {activeTab === 'linkage' && (
          <div className="rounded-2xl overflow-hidden">
            {isPro ? (
              <TopPriceLinkageTable limit={10} sessionType="REGULAR" windowDays={60} />
            ) : (
              <BlurredSection
                isPro={false}
                onUpgrade={() => setShowUpgradeModal(true)}
                title="Price Linkage - Premium"
              >
                <div className="dashboard-locked-preview rounded-2xl p-6 min-h-[420px]">
                  <div className="flex items-center gap-2 mb-3">
                    <Link2 className="w-5 h-5 text-brand-gold" />
                    <h2 className="text-xl font-semibold text-white">Top Price Linkage</h2>
                  </div>
                  <p className="text-white/60 text-sm mb-4">
                    Statistical linkage between Reddit metrics and 1h return.
                  </p>
                  <div className="space-y-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-10 rounded-lg bg-white/10 border border-white/10" />
                    ))}
                  </div>
                </div>
              </BlurredSection>
            )}
          </div>
        )}
      </div>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onSubscribe={() => {
          setShowUpgradeModal(false);
          navigate('/login?redirect=' + encodeURIComponent('/billing?checkout=1'));
        }}
      />
    </div>
  );
}

export default Dashboard; 
